import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QrCode, CheckCircle2, X } from "lucide-react";
import { formatINR, formatHour } from "@/lib/format";
import { toast } from "sonner";
import { formatApiError } from "@/lib/format";

const STATUS_TONE = {
  booked: "bg-[#D4B878]/15 text-[#E8D6A3] border-[#D4B878]/30",
  checked_in: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  completed: "bg-white/5 text-zinc-400 border-white/10",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/30",
};

export default function MyBookings() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/bookings/me");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const doAction = async (id, action) => {
    try {
      await api.post(`/bookings/${id}/${action}`);
      toast.success("Updated");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8" data-testid="my-bookings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
          Your bookings
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
          Sessions & reservations.
        </h1>
      </div>

      {items.length === 0 && (
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-12 text-center text-zinc-500">
          No bookings yet — explore hubs to book your first session.
        </div>
      )}

      <div className="space-y-4">
        {items.map((b) => (
          <div
            key={b.id}
            data-testid={`booking-row-${b.id}`}
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex items-center justify-between gap-6 flex-wrap"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <Badge className={`border ${STATUS_TONE[b.status] || ""}`} variant="outline">
                  {b.status.replace("_", " ")}
                </Badge>
                <span className="text-xs text-zinc-500">
                  {b.hub_name}
                </span>
              </div>
              <div className="text-lg font-bold">{b.resource_label}</div>
              <div className="text-sm text-zinc-500">
                {b.date} · {formatHour(b.start_hour)} —{" "}
                {formatHour(b.start_hour + b.duration_hours)} · {formatINR(b.price)}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setActive(b)}
                data-testid={`booking-qr-${b.id}`}
                className="bg-white/5 border-white/10 rounded-full"
              >
                <QrCode size={14} className="mr-2" /> QR
              </Button>
              {b.status === "booked" && (
                <>
                  <Button
                    onClick={() => doAction(b.id, "check-in")}
                    data-testid={`booking-check-in-${b.id}`}
                    className="bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full"
                  >
                    <CheckCircle2 size={14} className="mr-2" /> Check in
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => doAction(b.id, "cancel")}
                    data-testid={`booking-cancel-${b.id}`}
                    className="bg-white/5 border-white/10 rounded-full"
                  >
                    <X size={14} />
                  </Button>
                </>
              )}
              {b.status === "checked_in" && (
                <Button
                  onClick={() => doAction(b.id, "check-out")}
                  data-testid={`booking-check-out-${b.id}`}
                  className="bg-white text-black hover:bg-zinc-200 rounded-full"
                >
                  Check out
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {active && <QrModal booking={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function QrModal({ booking, onClose }) {
  const token = `MIRROR:${booking.id}`;
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=360x360&bgcolor=050505&color=FFFFFF&data=${encodeURIComponent(token)}`;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur p-6"
      onClick={onClose}
      data-testid="qr-modal"
    >
      <div
        className="rounded-3xl border border-white/10 bg-[#0c0c0c] p-8 w-full max-w-sm text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-2">
          Check-in QR
        </div>
        <div className="font-bold text-lg">{booking.resource_label}</div>
        <div className="text-xs text-zinc-500 mb-5">
          {booking.hub_name} · {booking.date}
        </div>
        <img src={qr} alt="QR" className="rounded-2xl mx-auto border border-white/10" />
        <Button
          onClick={onClose}
          className="mt-6 w-full bg-white text-black hover:bg-zinc-200 rounded-full"
          data-testid="qr-modal-close"
        >
          Close
        </Button>
      </div>
    </div>
  );
}
